import {createContext, useContext, useEffect, useReducer} from 'react';
import {useParams} from 'react-router-dom';
import {
    fetchAppointmentCategories,
    fetchCaregivers,
    fetchClinicById,
    fetchLocation,
    fetchScheduledStopsAtLocation,
    fetchVisitReasons
} from 'src/services/api/api';
import {NotFoundError} from 'src/services/api/implementations/fetch';
import {
    AppointmentCategory,
    CaregiverObject,
    Location,
    LocationDetails,
    LocationTimeSlot,
    ReasonFilterObject,
    ScheduledStop
} from 'src/services/api/types/types';

export enum DataActions {
    SET_LOADING = 'SET_LOADING',
    SET_ERROR = 'SET_ERROR',
    SET_LOCATION = 'SET_LOCATION',
    SET_CAREGIVER = 'SET_CAREGIVER',
    SET_VISIT_REASONS = 'SET_VISIT_REASONS',
    SET_APPOINTMENT_TYPES = 'SET_APPOINTMENT_TYPES',
    SET_LOCATION_STOPS = 'SET_LOCATION_STOPS',
    SET_LOCATION_TIME_SLOTS = 'SET_LOCATION_TIME_SLOTS',
    SET_APPOINTMENT_CATEGORIES = 'SET_APPOINTMENT_CATEGORIES',
    SET_HAS_DROP_IN = 'SET_HAS_DROP_IN',
    SET_HAS_BOOKING = 'SET_HAS_BOOKING'
}

type Action = {
    value:
        | boolean
        | null
        | Error
        | LocationDetails
        | CaregiverObject
        | Array<ReasonFilterObject>
        | Array<number>
        | Array<ScheduledStop>
        | Array<LocationTimeSlot>
        | Array<Array<AppointmentCategory>>;
    type: DataActions;
};

type Dispatch = (action: Action) => void;

type State = {
    loading: boolean;
    error: Error | null;
    location: LocationDetails | null;
    caregiver: CaregiverObject | null;
    visitReasons: Array<ReasonFilterObject>;
    appointmentCategories: Array<Array<AppointmentCategory>>;
    appointmentTypeIds: Array<number>;
    locationStops: Array<ScheduledStop>;
    locationTimeSlots: Array<LocationTimeSlot>;
    hasDropin: boolean;
    hasBooking: boolean;
};

type CountProviderProps = {children: React.ReactNode};

const LocationDetailsContext = createContext<{locationDataState: State; locationDispatch: Dispatch} | undefined>(
    undefined
);
function LocationDetailsReducer(state: State, action: Action): State {
    switch (action.type) {
        case DataActions.SET_LOADING: {
            return {...state, loading: action.value as boolean};
        }
        case DataActions.SET_ERROR: {
            return {...state, error: action.value as Error};
        }
        case DataActions.SET_LOCATION: {
            return {...state, location: action.value as LocationDetails};
        }
        case DataActions.SET_CAREGIVER: {
            return {...state, caregiver: action.value as CaregiverObject};
        }
        case DataActions.SET_VISIT_REASONS: {
            return {...state, visitReasons: action.value as Array<ReasonFilterObject>};
        }
        case DataActions.SET_APPOINTMENT_CATEGORIES: {
            return {...state, appointmentCategories: action.value as Array<Array<AppointmentCategory>>};
        }
        case DataActions.SET_APPOINTMENT_TYPES: {
            return {...state, appointmentTypeIds: action.value as Array<number>};
        }
        case DataActions.SET_LOCATION_STOPS: {
            return {...state, locationStops: action.value as Array<ScheduledStop>};
        }
        case DataActions.SET_LOCATION_TIME_SLOTS: {
            return {...state, locationTimeSlots: action.value as Array<LocationTimeSlot>};
        }
        case DataActions.SET_HAS_BOOKING: {
            return {...state, hasBooking: action.value as boolean};
        }
        case DataActions.SET_HAS_DROP_IN: {
            return {...state, hasDropin: action.value as boolean};
        }
        default: {
            throw new Error(`Unhandled action type: ${action.type}`);
        }
    }
}

function LocationDetailsProvider({children}: CountProviderProps) {
    const [locationDataState, locationDispatch] = useReducer(LocationDetailsReducer, {
        loading: true,
        error: null,
        location: null,
        caregiver: null,
        visitReasons: [],
        appointmentCategories: [],
        appointmentTypeIds: [],
        locationStops: [],
        locationTimeSlots: [],
        hasBooking: true,
        hasDropin: false
    });

    const {locationId} = useParams<{locationId: string}>();

    useEffect(() => {
        if (!locationId) {
            return;
        }

        const locationIdNum = parseInt(locationId || '', 10);

        if (Number.isNaN(locationIdNum)) {
            locationDispatch({type: DataActions.SET_LOADING, value: false});
            locationDispatch({type: DataActions.SET_ERROR, value: new NotFoundError('No such location')});
            return;
        }

        if (locationId !== `${locationIdNum}`) {
            window.history.replaceState(locationIdNum, '', `${locationIdNum}`);
        }

        const loadData = async () => {
            try {
                const location = await fetchLocation(parseInt(locationId, 10));
                locationDispatch({type: DataActions.SET_LOCATION, value: location});

                const locationStops = await fetchScheduledStopsAtLocation(
                    location.id,
                    new Date(),
                    new Date(Date.now() + 120 * 24 * 60 * 60 * 1000)
                );
                locationDispatch({type: DataActions.SET_LOCATION_STOPS, value: locationStops});

                const clinicIds = Array.from(new Set(locationStops.map((stop) => stop.clinicId)));

                const appointmentCategories = await Promise.all(
                    clinicIds.map((clinicId) => fetchAppointmentCategories(clinicId)).flat()
                );
                locationDispatch({
                    type: DataActions.SET_APPOINTMENT_CATEGORIES,
                    value: appointmentCategories
                });

                const clinicInfo = await Promise.all(clinicIds.map((clinicId) => fetchClinicById(clinicId)));

                const hasDropin = clinicInfo.some((c) => c?.hasDropin ?? false);
                const hasBooking = clinicInfo.some((c) => c?.hasTimebook ?? false);
                locationDispatch({type: DataActions.SET_HAS_DROP_IN, value: hasDropin});
                locationDispatch({type: DataActions.SET_HAS_BOOKING, value: hasBooking});

                const [caregivers, visitReasons] = await Promise.all([fetchCaregivers(), fetchVisitReasons()]);
                const caregiver = caregivers.find(({id}) => location && id === location.careGiver);
                locationDispatch({type: DataActions.SET_CAREGIVER, value: caregiver as CaregiverObject});
                locationDispatch({type: DataActions.SET_VISIT_REASONS, value: visitReasons});

                locationDispatch({type: DataActions.SET_LOADING, value: false});
            } catch (error) {
                locationDispatch({type: DataActions.SET_LOADING, value: false});
                locationDispatch({type: DataActions.SET_ERROR, value: error as Error});
            }
        };

        loadData();
    }, [locationId]);

    const value = {locationDataState, locationDispatch};
    return <LocationDetailsContext.Provider value={value}>{children}</LocationDetailsContext.Provider>;
}

function useLocationDetailsContext() {
    const context = useContext(LocationDetailsContext);
    if (context === undefined) {
        throw new Error('useDataContext must be used within a UiProvider');
    }

    return context;
}

function useOptionalLocationDetailsContext() {
    return useContext(LocationDetailsContext);
}

export {LocationDetailsProvider, useLocationDetailsContext, useOptionalLocationDetailsContext};
